import useUser from "components/common/useUser";
import Icon from "components/Icons/Icon";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { ESIM_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getEsimTranslations } from "components/website/marketing/i18n/pages/esim";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import React from "react";
import { OsTypes, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/Esim.scss";
import "../css/Website.scss";
import EsimSetupVisual from "../img/screenshots/esim/eSIM1.png";
import EsimRoamingVisual from "../img/screenshots/esim/eSIM2.png";
import EsimMultiDestVisual from "../img/screenshots/esim/eSIM4.png";
import EsimPointsVisual from "../img/screenshots/esim/eSIM5.png";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface EsimPageProps {
	lang?: string;
}

export default function EsimPage({ lang }: EsimPageProps) {
	const translations = getEsimTranslations(lang || "en");
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	return (
		<div className="esim-page" id="stippl-esim">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="hero-s-mark-left" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "esim.hero.chip", "Travel eSIM")}</div>
						<h1>
							{t(translations, "esim.hero.titlePrefix", "Your")}{" "}
							<span className="hero__accent">{t(translations, "esim.hero.titleAccent", "travel eSIM,")}</span>
							<br />
							{t(translations, "esim.hero.titleSuffix", "built into your trip.")}
						</h1>
						<p className="hr-hero__desc">
							{t(
								translations,
								"esim.hero.description",
								"Stippl puts a travel eSIM right inside your trip planner. Instant connectivity in 150+ countries, no roaming charges, no SIM swapping, and you can earn a free eSIM through Stippl points."
							)}
						</p>
						<div className="hero-ctas">
							<a
								href="/register"
								className="btn btn-pink"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "esim.hero.ctaPrimary", "Get a travel eSIM")}
							</a>
							<a href="#how-it-works" className="btn btn-outline-white">
								{t(translations, "esim.hero.ctaSecondary", "See how it works")}
							</a>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={EsimSetupVisual}
						alt={t(translations, "esim.hero.imageAlt", "Travel eSIM screenshot")}
						loading="eager"
					/>
				</div>
			</section>

			{/* FEATURES INTRO */}
			<section className="features-intro">
				<div className="section-wrapper">
					<div className="features-intro-header">
						<h2>{t(translations, "esim.featuresIntro.heading", "Your travel eSIM, built into your trip")}</h2>
						<p>
							{t(
								translations,
								"esim.featuresIntro.description",
								"Stippl is the only app that combines your itinerary, travel budget planner and travel eSIM in one place. Plan your trip and stay connected, without switching between apps or providers."
							)}
						</p>
					</div>
					<div className="features-grid">
						<div className="feature-card">
							<div className="feature-card-icon icon-pink"><Icon name="esim" size={22} fill="var(--main2)" /></div>
							<h3>{t(translations, "esim.features.instantActivation.title", "Instant travel eSIM activation")}</h3>
							<p>
								{t(
									translations,
									"esim.features.instantActivation.description",
									"Activate your travel eSIM before you leave home. Scan a QR code and you're connected the moment you land: no queues, no airport SIM kiosks."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green"><Icon name="database" size={22} pathProps={{ stroke: "var(--main1)" }} /></div>
							<h3>{t(translations, "esim.features.cheaperRoaming.title", "Far cheaper than roaming")}</h3>
							<p>
								{t(
									translations,
									"esim.features.cheaperRoaming.descriptionPrefix",
									"Skip expensive carrier roaming fees. A travel eSIM costs a fraction of what roaming does, and you can log the cost directly in your Stippl"
								)}{" "}
								<a href={localizeHref("/expense-tracker", lang as MarketingLangCode)} style={{ color: "var(--main2)", fontWeight: 500 }}>
									{t(translations, "esim.features.cheaperRoaming.linkText", "expense tracker")}
								</a>
								.
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-pink"><Icon name="globe" size={22} fill="var(--main2)" /></div>
							<h3>{t(translations, "esim.features.multiDestination.title", "Multi-destination eSIM")}</h3>
							<p>
								{t(
									translations,
									"esim.features.multiDestination.description",
									"Visiting multiple countries? Manage your travel eSIM across all destinations from a single dashboard, perfectly synced with your trip itinerary."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-purple"><Icon name="phone" size={22} fill="var(--main4)" /></div>
							<h3>{t(translations, "esim.features.keepNumber.title", "Keep your number")}</h3>
							<p>
								{t(
									translations,
									"esim.features.keepNumber.description",
									"Your home SIM stays active. A travel eSIM runs digitally alongside it: no SIM swap, no losing your regular number, no missed calls from home."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-pink"><Icon name="chart" size={22} pathProps={{ stroke: "var(--main2)" }} /></div>
							<h3>{t(translations, "esim.features.dataUsage.title", "Real-time data usage")}</h3>
							<p>
								{t(
									translations,
									"esim.features.dataUsage.description",
									"Track how much data you've used and what's left, directly from within the Stippl app. No surprises, no overage bills."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green">✦</div>
							<h3>{t(translations, "esim.features.earnPoints.title", "Earn a free eSIM with Stippl points")}</h3>
							<p>
								{t(
									translations,
									"esim.features.earnPoints.description",
									"Invite friends to Stippl or book accommodations through the app to earn Stippl points. Collect enough and redeem them for a completely free travel eSIM, no payment needed."
								)}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: EASY SETUP */}
			<section className="bg-white">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "esim.setup.chip", "Instant activation")}</div>
							<h2>{t(translations, "esim.setup.heading", "The easiest travel eSIM setup. Full stop.")}</h2>
							<p>
								{t(
									translations,
									"esim.setup.description",
									"Buy your eSIM inside the Stippl app, install it with a single QR code scan before you fly, and you're connected the moment you land. No queues, no SIM trays, no stress."
								)}
							</p>
							<ul className="feature-bullets">
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.setup.bullet1", "Choose your travel eSIM plan inside the Stippl app")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.setup.bullet2", "Scan the QR code to install the eSIM on your phone")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.setup.bullet3", "Land in your destination and connect automatically")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.setup.bullet4", "No SIM swap, no roaming charges, no stress")}
								</li>
							</ul>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "esim.setup.cta", "Activate my travel eSIM →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={EsimSetupVisual} alt={t(translations, "esim.setup.imageAlt", "eSIM activation screenshot")} />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: ROAMING SAVINGS */}
			<section className="bg-ice">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "esim.roaming.chip", "No roaming charges")}</div>
							<h2>{t(translations, "esim.roaming.heading", "Stop paying roaming charges. Get an eSIM for travel instead.")}</h2>
							<p>
								{t(
									translations,
									"esim.roaming.description",
									"International data roaming from your home carrier can cost 10× more than a local eSIM. Stippl connects you with local data rates in every country: so you pay what locals pay, not what your carrier charges abroad."
								)}
							</p>
							<ul className="feature-bullets">
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.roaming.bullet1", "Local data speeds, not throttled roaming")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.roaming.bullet2", "Fixed price per GB, no surprise overage fees")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.roaming.bullet3", "Add data mid-trip without changing plans")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.roaming.bullet4", "Costs tracked in your travel budget planner")}
								</li>
							</ul>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "esim.roaming.cta", "Save on roaming now →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={EsimRoamingVisual} alt={t(translations, "esim.roaming.imageAlt", "Roaming savings screenshot")} />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: MULTI-DESTINATION */}
			<section className="bg-white">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "esim.multiDest.chip", "Multi-destination")}</div>
							<h2>{t(translations, "esim.multiDest.heading", "One trip. Multiple countries. One travel eSIM dashboard.")}</h2>
							<p>
								{t(
									translations,
									"esim.multiDest.description",
									"Whether you're island-hopping in Southeast Asia or doing a multi-country road trip through Europe, Stippl gives you one place to manage your eSIM data across every destination, right inside your itinerary."
								)}
							</p>
							<ul className="feature-bullets">
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.multiDest.bullet1", "eSIM for travel covers 150+ countries worldwide")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.multiDest.bullet2", "Multi-destination eSIM plans for Asia, Europe & more")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.multiDest.bullet3", "See data usage per country inside the app")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✓</span>
									{t(translations, "esim.multiDest.bullet4", "eSIM automatically switches as you cross borders")}
								</li>
							</ul>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "esim.multiDest.cta", "Explore destinations →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={EsimMultiDestVisual} alt={t(translations, "esim.multiDest.imageAlt", "Multi-destination eSIM screenshot")} />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: EARN FREE ESIM */}
			<section className="bg-ice" id="stippl-points">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "esim.points.chip", "Stippl points")}</div>
							<h2>{t(translations, "esim.points.heading", "Get a free travel eSIM. Earned, not paid for.")}</h2>
							<p>
								{t(
									translations,
									"esim.points.description",
									"Stippl rewards you for planning and sharing trips. Earn Stippl points through referrals, bookings and activity in the app, and redeem them for a free eSIM on your next adventure."
								)}
							</p>
							<ul className="feature-bullets">
								<li>
									<span className="bullet-dot dot-pink">✦</span>
									{t(translations, "esim.points.bullet1", "Invite friends to Stippl → earn Stippl points")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✦</span>
									{t(translations, "esim.points.bullet2", "Book accommodations through Stippl → earn Stippl points")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✦</span>
									{t(translations, "esim.points.bullet3", "Redeem points for a free travel eSIM, no payment needed")}
								</li>
								<li>
									<span className="bullet-dot dot-pink">✦</span>
									{t(translations, "esim.points.bullet4", "Points never expire, save up for bigger trips")}
								</li>
							</ul>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "esim.points.cta", "Start earning Stippl points →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={EsimPointsVisual} alt={t(translations, "esim.points.imageAlt", "Stippl points eSIM screenshot")} />
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="pink"
				title={t(translations, "esim.howToUse.name", ESIM_CONTENT.howToUse.name)}
				description={t(translations, "esim.howToUse.description", ESIM_CONTENT.howToUse.description)}
				steps={ESIM_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `esim.howToSteps.${index}.name`, step.name),
					text: t(translations, `esim.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "esim.howItWorks.cta", "Get a travel eSIM →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-left">
							<div className="pro-callout-label">{t(translations, "esim.pro.label", "Stippl Pro")}</div>
							<h3>{t(translations, "esim.pro.heading", "Get more from your travel eSIM")}</h3>
							<p>
								{t(
									translations,
									"esim.pro.description",
									"The eSIM marketplace is free for every traveller. Pro members get exclusive discounts, faster point earning and priority support."
								)}
							</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "esim.pro.feature1Bold", "Exclusive discounted eSIM plans")}</strong>
										{t(translations, "esim.pro.feature1Text", ": Pro members unlock cheaper data rates across all destinations")}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "esim.pro.feature2Bold", "2× Stippl points")}</strong>
										{t(translations, "esim.pro.feature2Text", ": earn double the points on every booking and referral to reach your free eSIM faster")}
									</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<svg className="pro-price-card-smark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d={S_PATH} fill="white" />
							</svg>
							<div className="pro-price-label">{t(translations, "esim.pro.priceLabel", "Starting from")}</div>
							<div className="pro-price-amount">€3</div>
							<div className="pro-price-period">{t(translations, "esim.pro.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "esim.pro.priceBtn", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "esim.pro.priceNote", "Cancel anytime · No commitment")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section>
				<div className="section-wrapper">
					<FAQSection
						heading={t(translations, "esim.faq.heading", "Frequently asked questions about our travel eSIM")}
						items={ESIM_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `esim.faqItems.${index}.question`, item.question),
							answer: t(translations, `esim.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA STRIP */}
			<section className="cta-section">
				<svg className="cta-s" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="cta-s-right" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="cta-inner">
					<div className="cta-chip">{t(translations, "esim.ctaStrip.chip", "Travel eSIM")}</div>
					<h2>{t(translations, "esim.ctaStrip.heading", "The travel eSIM built into your trip planner.")}</h2>
					<p className="cta-sub">
						{t(
							translations,
							"esim.ctaStrip.description",
							"No roaming. No SIM swapping. Earn a free eSIM with Stippl points, just by inviting friends or booking accommodations."
						)}
					</p>
					<div className="cta-btns">
						<a
							href="/register"
							className="btn btn-pink"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "esim.ctaStrip.ctaPrimary", "Get a travel eSIM")}
						</a>
						<a
							href="/register"
							className="btn btn-outline-white"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "esim.ctaStrip.ctaSecondary", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
